/**
 * useTutorApplicationState — la page de candidature : sa relecture et ses gestes, ceux de l'ancien
 * `TutorApplicationScreen` :
 *
 *  - le compte relu à chaque retour sur l'écran (`getUser`, `useFocusEffect`) ;
 *  - à l'étape 1, l'intro du champion de l'accueil poussée à l'ouverture (300 ms après) ;
 *  - « Commencer » / « Continuer » : au web, sur le navigateur d'un téléphone ou sur Safari, une étape
 *    vidéo ouvre la boîte « Télécharge l'application mobile » ; sinon le profil à l'étape 1, l'intro
 *    du champion de l'étape ensuite (qui ouvre l'entretien, avec les paramètres posés pour lui) ;
 *  - « Télécharger » : la fiche de l'app (Android au navigateur d'un Android, Apple sinon), dans la
 *    même page comme l'ancien ;
 *  - la pastille de compte : `account`.
 */
import { useFocusEffect, useNavigation } from "@react-navigation/native"
import Constants from "expo-constants"
import { useCallback, useEffect, useRef, useState } from "react"
import { Platform } from "react-native"
import { useDispatch, useSelector } from "react-redux"

import { apiRequest } from "../../../../../../data/redux/actions/api"
import { putNextScreenParams } from "../../../../../../data/redux/actions/navigation"
import { authService } from "../../../../../../data/services/authService"
import { liveStoreFor, liveStoreUrl } from "../../../../adapters/student/LiveEntry/liveBrowserAdapter"
import { appRequiredFor } from "../../../../adapters/tutor/Application/tutorApplicationAdapter"
import { type TutorApplicationViewModel } from "../../../../view-models/tutor/Application/tutorApplicationViewModel"
import { browserUserAgent } from "../../../hooks/openLiveAppStore"
import { type UITutorApplicationAppRequired } from "../../../types/tutorCandidature"

type Navigation = { push: (route: string, params?: object) => void }

type StoreState = { data?: { staticData?: { android_app_url?: string | null; ios_app_url?: string | null } } }

type ExpoStoreConfig = { ios?: { appStoreUrl?: string }; android?: { playStoreUrl?: string } } | null

/** Le délai avant l'intro du champion de l'accueil, à l'ouverture de l'étape 1 (l'ancien `setTimeout`). */
export const FIRST_INTRO_DELAY_MS = 300

export function useTutorApplicationState(vm: TutorApplicationViewModel) {
    const dispatch = useDispatch()
    const navigation = useNavigation() as unknown as Navigation
    const storeUrls = useSelector((state: StoreState) => state?.data?.staticData)
    const [appRequired, setAppRequired] = useState<UITutorApplicationAppRequired | null>(null)

    useFocusEffect(
        useCallback(() => {
            dispatch(apiRequest(authService.getUser) as never)
        }, [dispatch])
    )

    // L'ancien la poussait au montage, l'étape déjà lue ; la coquille du design system peut se monter
    // avant elle (juste après la connexion) : on la pousse à la première étape lue, une fois.
    const firstStepReadRef = useRef(false)
    const introTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    useEffect(() => {
        if (firstStepReadRef.current || vm.currentStep <= 0) return
        firstStepReadRef.current = true
        if (vm.currentStep !== 1) return
        introTimerRef.current = setTimeout(
            () => navigation.push("intro", { name: "abou", introSuffix: "_application_interview_intro" }),
            FIRST_INTRO_DELAY_MS
        )
    }, [navigation, vm.currentStep])
    useEffect(() => () => clearTimeout(introTimerRef.current ?? undefined), [])

    const onStartStep = () => {
        const required = appRequiredFor({ isWeb: Platform.OS === "web", userAgent: browserUserAgent() })
        if (required != null && vm.currentStepIsVideo) {
            setAppRequired(required)
            return
        }
        const destination = vm.destination
        if (destination == null) return
        if (destination.kind === "profile") {
            navigation.push("application-personal-infos")
            return
        }
        dispatch(putNextScreenParams(destination.nextScreenParams) as never)
        navigation.push("intro", destination.intro)
    }

    const onDownloadApp = () => {
        const expo = Constants.expoConfig as ExpoStoreConfig
        const url = liveStoreUrl(liveStoreFor(browserUserAgent()), {
            androidAppUrl: storeUrls?.android_app_url,
            iosAppUrl: storeUrls?.ios_app_url,
            buildAndroid: expo?.android?.playStoreUrl,
            buildIos: expo?.ios?.appStoreUrl,
        })
        setAppRequired(null)
        if (typeof window !== "undefined") window.location.href = url
    }

    return {
        onStartStep,
        onOpenAccount: () => navigation.push("account"),
        appRequired,
        onDownloadApp,
        onDismissAppRequired: () => setAppRequired(null),
    }
}
